import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Toaster } from './components/ui/toaster';
import Header from "./components/Header";
import Hero from "./components/Hero";
import CallDemos from "./components/CallDemos";
import VideoSection from "./components/VideoSection";
import Features from "./components/Features";
import HowItWorks from "./components/HowItWorks";
import Industries from "./components/Industries";
import Testimonials from "./components/Testimonials";
import Pricing from "./components/Pricing";
import Footer from "./components/Footer";
import Login from "./components/Login";
import Register from "./components/Register";
import Dashboard from "./components/Dashboard";
import BusinessSetup from "./components/BusinessSetup";
import PhoneSetup from "./components/PhoneSetup";
import CallHistory from "./components/CallHistory";
import Analytics from "./components/Analytics";
import FAQManager from "./components/FAQManager";
import Settings from "./components/Settings";
import Teams from "./components/TeamManagement";
import CRM from "./components/CRM";
import Invoices from "./components/Invoices";
import Products from "./components/ProductsCatalog";
import SalesOutreach from "./components/SalesOutreach";
import Tickets from "./components/Tickets";
import ConversationInsights from "./components/ConversationInsights";
import AdminLayout from "./components/admin/AdminLayout";
import AdminDashboard from "./components/admin/AdminDashboard";
import AdminUsers from "./components/admin/AdminUsers";
import AdminAccounts from "./components/admin/AdminAccounts";
import AdminPromoCodes from "./components/admin/AdminPromoCodes";
import AdminAffiliates from "./components/admin/AdminAffiliates";
import AdminCommunication from "./components/admin/AdminCommunication";
import AdminData from "./components/admin/AdminData";
import AdminMaintenance from "./components/admin/AdminMaintenance";
import AdminSupport from "./components/admin/AdminSupport";
import HelpSupport from "./components/HelpSupport";
import AffiliateSignup from "./components/AffiliateSignup";
import AffiliateDashboard from "./components/AffiliateDashboard";
import ReferralRedirect from "./components/ReferralRedirect";
import InvoicePayment from "./components/InvoicePayment";
import AppLayout from "./components/AppLayout";
import ChatAgent from "./components/ChatAgent";
import InboundCalls from "./components/InboundCalls";
import OutboundCalls from "./components/OutboundCalls";
import HoldMusic from "./components/HoldMusic";
import Appointments from "./components/Appointments";
import IntegrationsWorkflows from "./components/IntegrationsWorkflows";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? children : <Navigate to="/login" replace />;
};

const ImpersonationBanner = () => {
  const isImpersonating = localStorage.getItem('impersonating') === 'true';
  if (!isImpersonating) return null;
  const stopImpersonating = () => {
    const adminToken = localStorage.getItem('admin_token');
    localStorage.removeItem('impersonating');
    localStorage.removeItem('admin_token');
    if (adminToken) localStorage.setItem('token', adminToken);
    window.location.href = '/admin/users';
  };
  return (
    <div className="fixed top-0 left-0 right-0 z-[60] bg-amber-500 text-black text-center py-2 text-sm font-medium" data-testid="impersonation-banner">
      You are impersonating a user.
      <button onClick={stopImpersonating} className="ml-3 px-3 py-0.5 bg-black text-amber-400 rounded-full text-xs font-bold hover:bg-gray-900" data-testid="stop-impersonating-btn">
        Stop Impersonating
      </button>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
      <BrowserRouter>
        <ImpersonationBanner />
        <Toaster />
        <Routes>
          <Route path="/" element={<><Header /><main className="pt-20 bg-gradient-to-br from-[#0a1628] via-[#0d1f35] to-[#0a1628] min-h-screen"><Hero /><CallDemos /><VideoSection /><Features /><HowItWorks /><Industries /><Testimonials /><Pricing /><Footer /></main></>} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/business-setup" element={<BusinessSetup />} />
            <Route path="/phone-setup" element={<PhoneSetup />} />
            <Route path="/call-history" element={<CallHistory />} />
            <Route path="/analytics" element={<Analytics />} />
            <Route path="/faq-manager" element={<FAQManager />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/teams" element={<Teams />} />
            <Route path="/crm" element={<CRM />} />
            <Route path="/invoices" element={<Invoices />} />
            <Route path="/products" element={<Products />} />
            <Route path="/sales-outreach" element={<SalesOutreach />} />
            <Route path="/tickets" element={<Tickets />} />
            <Route path="/conversation-insights" element={<ConversationInsights />} />
            <Route path="/chat-agent" element={<ChatAgent />} />
            <Route path="/inbound-calls" element={<InboundCalls />} />
            <Route path="/outbound-calls" element={<OutboundCalls />} />
            <Route path="/hold-music" element={<HoldMusic />} />
            <Route path="/appointments" element={<Appointments />} />
            <Route path="/integrations" element={<IntegrationsWorkflows />} />
            <Route path="/help-support" element={<HelpSupport />} />
          </Route>
          {/* Affiliate routes */}
          <Route path="/affiliate" element={<AffiliateSignup />} />
          <Route path="/affiliate/dashboard/:username" element={<AffiliateDashboard />} />
          <Route path="/ref/:username" element={<ReferralRedirect />} />
          <Route path="/pay/:shareToken" element={<InvoicePayment />} />
          {/* Admin routes */}
          <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
            <Route index element={<AdminDashboard />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="accounts" element={<AdminAccounts />} />
            <Route path="promo-codes" element={<AdminPromoCodes />} />
            <Route path="affiliates" element={<AdminAffiliates />} />
            <Route path="communication" element={<AdminCommunication />} />
            <Route path="support" element={<AdminSupport />} />
            <Route path="data" element={<AdminData />} />
            <Route path="maintenance" element={<AdminMaintenance />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
